import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import PublicHeader from "../components/layout/public-header/PublicHeader";
import useApps from "./hooks/useApps";
import styles from "./styles/AppsPage.module.css";

const AppsPage = () => {
  const { apps, loading } = useApps();

  const live = apps.filter((a) => a.status === "live");
  const comingSoon = apps.filter((a) => a.status === "coming_soon");

  return (
    <div className={styles.page}>
      <Helmet>
        <title>Apps | Xeniapps</title>
        <meta name="description" content="A collection of apps built by Marco Zak Ciuffini — freelance React Native and full stack developer based in Birmingham, UK." />
        <link rel="canonical" href="https://www.xeniapps.com/apps" />
      </Helmet>
      <PublicHeader links={[
        { label: "Home", to: "/" },
        { label: "About", to: "/" },
        { label: "Contact", to: "/" },
      ]} />

      <div className={styles.hero}>
        <h1 className={styles.heroTitle}>
          A small collection<br />of useful apps.
        </h1>
        <p className={styles.heroSub}>Built with care. Made for everyday life.</p>
      </div>

      <div className={styles.section}>
        {loading && <p className={styles.emptyState}>Loading...</p>}

        {!loading && apps.length === 0 && (
          <p className={styles.emptyState}>Nothing here yet — check back soon.</p>
        )}

        {!loading && live.length > 0 && (
          <>
            <p className={styles.sectionLabel}>Available now</p>
            <div className={styles.grid}>
              {live.map((app) => (
                <div key={app.id} className={styles.card}>
                  <Link to={`/apps/${app.slug}`} className={styles.cardLink}>
                  <div className={styles.cardTop}>
                    {app.icon_url ? (
                      <img src={app.icon_url} alt={app.name} className={styles.icon} />
                    ) : (
                      <div className={styles.iconPlaceholder}>📱</div>
                    )}
                    <div className={styles.cardInfo}>
                      <div className={styles.cardHeader}>
                        <span className={styles.cardName}>{app.name}</span>
                      </div>
                      {app.tagline && (
                        <p className={styles.cardTagline}>{app.tagline}</p>
                      )}
                    </div>
                  </div>
                  {app.description && (
                    <p className={styles.cardDesc}>{app.description}</p>
                  )}
                  </Link>
                  {app.download_url && (
                    <a href={app.download_url} className={styles.downloadBtn}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M5 20h14v-2H5v2zm7-18v12l-4-4-1.4 1.4L12 17l5.4-5.6L16 10l-4 4V2h-2z" />
                      </svg>
                      <span>{app.download_label || "Download for Android"}</span>
                      {app.download_version && (
                        <span className={styles.downloadMeta}>
                          v{app.download_version}
                          {app.download_updated_at &&
                            ` · ${new Date(app.download_updated_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`}
                        </span>
                      )}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {!loading && comingSoon.length > 0 && (
          <>
            <p className={styles.sectionLabel}>Coming soon</p>
            <div className={styles.grid}>
              {comingSoon.map((app) => (
                <div key={app.id} className={`${styles.card} ${styles.cardDimmed}`}>
                  <div className={styles.cardTop}>
                    {app.icon_url ? (
                      <img src={app.icon_url} alt={app.name} className={styles.icon} />
                    ) : (
                      <div className={styles.iconPlaceholder}>📱</div>
                    )}
                    <div className={styles.cardInfo}>
                      <div className={styles.cardHeader}>
                        <span className={styles.cardName}>{app.name}</span>
                        <span className={styles.badgeSoon}>Coming soon</span>
                      </div>
                      {app.tagline && (
                        <p className={styles.cardTagline}>{app.tagline}</p>
                      )}
                    </div>
                  </div>
                  {app.description && (
                    <p className={styles.cardDesc}>{app.description}</p>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <footer className={styles.footer}>
        <p>© {new Date().getFullYear()} Xeniapps</p>
      </footer>
    </div>
  );
};

export default AppsPage;
